/**
 * Atlas URLs in both directions: builders that turn an entity id into an in-app
 * path, and parsers that turn a URL back into the object it points at.
 *
 * Imported by the frontend (to render router-aware links) and the backend (so
 * tools can emit a `url` field without hard-coding strings). Intentionally free
 * of React / React Router dependencies so server-side code can call it. For the
 * parameterized React Router route *template* (with `:id` placeholders), use
 * `client/src/constants/routes.ts`.
 */
import {
  ClientPageTabNames,
  extractRouteParams,
  getRouteLabel,
  RouteKey,
  ROUTES,
} from '../constants/routes'
import { isUuid } from './helpers'

// Base for resolving a relative URL. Browser-only APIs are guarded so this
// module stays callable from server code and tests.
const urlBase = (): string =>
  typeof window === 'undefined' ? 'http://localhost' : window.location.origin

export interface AtlasDocUrlOptions {
  /** Stable slug (preferred — survives title edits via slug_aliases) */
  slug?: string | null
  /** Fallback identity when slug isn't available */
  docId?: string | null
  /** Optional in-doc anchor */
  sectionId?: string | null
}

/**
 * Atlas AI Docs viewer. Prefers `?slug=X` over `?id=Y` when both are supplied
 * — the slug-based URL is stable across title changes, and the Docs route
 * resolves either. Appends `#sectionId` for in-doc anchor scroll when given.
 */
function docUrl({ slug, docId, sectionId }: AtlasDocUrlOptions): string | null {
  const hash = sectionId ? `#${sectionId}` : ''
  if (slug) return `/atlas-ai/docs?slug=${encodeURIComponent(slug)}${hash}`
  if (docId) return `/atlas-ai/docs?id=${encodeURIComponent(docId)}${hash}`
  return null
}

/**
 * A task's detail panel. Keyed on `?taskId` rather than a path because the panel
 * is hosted by both My Tasks and the project page — the same param
 * `parseAtlasObjectUrl` below reads back out, so a link built here round-trips
 * into a task chip.
 */
const taskUrl = (id: string): string => `/my-tasks?taskId=${encodeURIComponent(id)}`

const taskProjectUrl = (id: string): string =>
  ROUTES.TASK_PROJECT_PAGE.replace(':id', encodeURIComponent(id))

const clientUrl = (id: string): string => `/clients/${id}`

/** One error in the Error Center, open in its detail drawer. */
const errorTriageUrl = (id: string): string => `/error-center?id=${encodeURIComponent(id)}`

/**
 * An amendment on its client's Activity tab. The page has no route of its own —
 * `?amendmentId` is what makes the Amendments table auto-expand that row (see
 * ClientAmendments.tsx) — so every link to an amendment has to carry the tab and
 * scroll target too.
 */
const amendmentUrl = (clientId: string, amendmentId?: string | null): string => {
  const amendmentParam = amendmentId ? `&amendmentId=${encodeURIComponent(amendmentId)}` : ''
  return `${clientUrl(clientId)}?tab=${encodeURIComponent(
    ClientPageTabNames.Activity,
  )}&scrollTo=amendments${amendmentParam}`
}

/** An amendment analysis — the page people drive the amendment through. */
const amendmentAnalysisUrl = (clientId: string, analysisId: string): string =>
  `${clientUrl(clientId)}/amendment-analysis/${analysisId}`

const prospectUrl = (id: string): string => `/prospects/${id}`
const dealSummaryUrl = (id: string): string => `${prospectUrl(id)}/summary`
const analysisQueueUrl = (id: string): string => `${prospectUrl(id)}/analysis-queue`
const entityUrl = (id: string): string => `/entity/${id}`
const bbcUrl = (id: string): string => `/bbc/${id}/summary`
const dueDiligenceUrl = (id: string): string => `/due-diligence/${id}`
const crmCompanyUrl = (id: string): string => ROUTES.CRM_COMPANY_PAGE.replace(':id', id)
const crmPersonUrl = (id: string): string => ROUTES.CRM_PERSON_PAGE.replace(':id', id)
const crmOrganizationUrl = (id: string): string => ROUTES.CRM_ORGANIZATION_PAGE.replace(':id', id)

export const atlasUrls = {
  doc: docUrl,
  task: taskUrl,
  taskProject: taskProjectUrl,
  client: clientUrl,
  errorTriage: errorTriageUrl,
  amendment: amendmentUrl,
  amendmentAnalysis: amendmentAnalysisUrl,
  prospect: prospectUrl,
  dealSummary: dealSummaryUrl,
  analysisQueue: analysisQueueUrl,
  entity: entityUrl,
  bbc: bbcUrl,
  dueDiligence: dueDiligenceUrl,
  crmCompany: crmCompanyUrl,
  crmPerson: crmPersonUrl,
  crmOrganization: crmOrganizationUrl,
}

/**
 * Decide whether a URL should be handled client-side by React Router (same
 * origin / relative) vs. opened in a new tab as a plain external link. Pure
 * string test — callers check this before choosing <Link> vs <a>.
 */
export function isInternalAtlasUrl(href: string | null | undefined): boolean {
  if (!href) return false
  // Without a page to resolve against, only a path or a link on this app counts.
  if (typeof window === 'undefined') return atlasPathOf(href) !== null
  // Resolved, so `?taskId=…` and `#section` count and `/\evil.example` does not.
  try {
    return new URL(href, window.location.origin).origin === window.location.origin
  } catch {
    return false
  }
}

/**
 * The in-app path a link opens, or null when it is not a page of this app.
 * Resolved rather than prefix-checked: `/\evil.example` starts with one slash but
 * is another origin to the browser, and the router would follow it there.
 * `anyHost` keeps the path of an http(s) link on any host, for links the app made
 * with the wrong one (a misconfigured `localhost:3000`); the path still opens here.
 */
export function atlasPathOf(
  href: string | null | undefined,
  { origin = urlBase(), anyHost = false }: { origin?: string; anyHost?: boolean } = {},
): string | null {
  const trimmed = href?.trim()
  if (!trimmed || !/^(\/(?!\/)|https?:\/\/)/i.test(trimmed)) return null
  try {
    const target = new URL(trimmed, origin)
    if (!anyHost && target.origin !== new URL(origin).origin) return null
    if (target.pathname.startsWith('//')) return null
    return `${target.pathname}${target.search}${target.hash}`
  } catch {
    return null
  }
}

type RouteParamValue = string | number | null | undefined

/** A route's path with its `:params` filled in, or the names of those still missing. */
export function fillRoutePath(
  route: RouteKey,
  params: Record<string, RouteParamValue>,
): { path: string } | { missing: string[] } {
  const template = ROUTES[route]
  const names = extractRouteParams(template)
  const missing = names.filter((name) => params[name] === undefined || params[name] === null)
  if (missing.length > 0) return { missing }
  return {
    path: names.reduce(
      (path, name) => path.replace(`:${name}`, encodeURIComponent(String(params[name]))),
      template,
    ),
  }
}

/**
 * Query params added to an in-app link, replacing any of the same name it
 * already carries. Its `#fragment` stays a fragment rather than ending up inside
 * a query value.
 */
export function withQueryParams(
  link: string,
  query: Record<string, string | number | boolean | null | undefined> = {},
): string {
  const [beforeHash, ...hashParts] = link.split('#')
  const [path, search = ''] = beforeHash.split('?')
  const params = new URLSearchParams(search)
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null) params.set(key, String(value))
  })
  const pairs = [...params.entries()].map(
    ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
  )
  const hash = hashParts.length > 0 ? `#${hashParts.join('#')}` : ''
  return `${path}${pairs.length > 0 ? `?${pairs.join('&')}` : ''}${hash}`
}

/**
 * Strip the origin off an internal Atlas URL so the result can be handed to
 * React Router's `to`, which treats a full `https://…` string as a relative
 * path and would navigate somewhere nonsensical. Returns `href` unchanged when
 * it's already a path (or unparseable).
 */
export function toAtlasRouterPath(href: string): string {
  if (href.startsWith('/')) return href
  try {
    const { pathname, search, hash } = new URL(href, urlBase())
    return `${pathname}${search}${hash}`
  } catch {
    return href
  }
}

/**
 * The kinds of Atlas object a URL can point at. Values are chosen to match
 * `MentionType`'s spelling where the two overlap, so the vocabularies can be
 * reconciled without a data migration once more types are resolvable. `Person`
 * follows `SearchType.Person` — a CRM person row, not a portal user account.
 */
export enum AtlasObjectType {
  Task = 'Task',
  Person = 'Person',
  Client = 'Client',
  Prospect = 'Prospect',
  BBC = 'BBC',
  Organization = 'Organization',
  Entity = 'Entity',
  Company = 'Company',
  OngoingReporting = 'OngoingReporting',
  TaskProject = 'TaskProject',
  Participant = 'Participant',
  DueDiligence = 'DueDiligence',
  /** A teammate. No page of its own: reached only through an @mention. */
  User = 'User',
}

export interface IAtlasObjectRef {
  type: AtlasObjectType
  id: string
}

/**
 * Which routes address which object. A list rather than a map because a type can
 * have more than one route — an organization has both the unified page and the
 * CRM one. Task is absent on purpose: it is keyed on a query param instead.
 *
 * Pointing at `ROUTES` rather than repeating the paths means a route rename
 * moves the parser with it.
 */
const OBJECT_ROUTES: Array<{ type: AtlasObjectType; template: string }> = [
  { type: AtlasObjectType.Person, template: ROUTES.CRM_PERSON_PAGE },
  { type: AtlasObjectType.Client, template: ROUTES.CLIENT_PAGE },
  { type: AtlasObjectType.Prospect, template: ROUTES.PROSPECT_PAGE },
  { type: AtlasObjectType.BBC, template: ROUTES.BBC_SUMMARY },
  { type: AtlasObjectType.Organization, template: ROUTES.ORGANIZATION_PAGE },
  { type: AtlasObjectType.Organization, template: ROUTES.CRM_ORGANIZATION_PAGE },
  { type: AtlasObjectType.Entity, template: ROUTES.ENTITY_PAGE },
  { type: AtlasObjectType.Company, template: ROUTES.CRM_COMPANY_PAGE },
  { type: AtlasObjectType.OngoingReporting, template: ROUTES.ONGOING_REPORTING },
  // Two reporting pages spell the subtype before the id, so the one-segment
  // prefix above would read that word as the id and reject it.
  { type: AtlasObjectType.OngoingReporting, template: ROUTES.ONGOING_REPORTING_SALES_BY_SKU },
  { type: AtlasObjectType.OngoingReporting, template: ROUTES.ONGOING_REPORTING_AR_GENERAL_LEDGER },
  { type: AtlasObjectType.TaskProject, template: ROUTES.TASK_PROJECT_PAGE },
  { type: AtlasObjectType.Participant, template: ROUTES.PARTICIPANTS_PAGE },
  // A diligence page reads the same client_info row as a Client link, but its own
  // services admit BD, whom the client gate excludes — so it is its own type
  // rather than a second route onto Client.
  { type: AtlasObjectType.DueDiligence, template: ROUTES.DUE_DILIGENCE },
  // Routes whose :id is a record already resolved above, reached by a different
  // page: client setup is a client_info, entity settings an entity.
  { type: AtlasObjectType.Client, template: ROUTES.CLIENT_SETUP },
  { type: AtlasObjectType.Entity, template: ROUTES.ENTITY_SETTINGS },
]

/** Path segments preceding the `:id` placeholder — `/bbc/:id/summary` → `['bbc']`. */
const templatePrefix = (template: string): string[] => {
  const segments = template.split('/').filter(Boolean)
  const idIndex = segments.findIndex((segment) => segment.startsWith(':'))
  return idIndex === -1 ? segments : segments.slice(0, idIndex)
}

// Longest prefix first so a more specific route wins if two ever nest.
const OBJECT_PATH_MATCHERS = OBJECT_ROUTES.map(({ type, template }) => ({
  type,
  prefix: templatePrefix(template),
})).sort((a, b) => b.prefix.length - a.prefix.length)

/**
 * The unified organization page is backed by two different tables: `:id` is an
 * `organization` by default and an `entity_info` under `?source=entity` (see
 * OrganizationPage and the PhotoSubjectType.Organizations note). Reading the id
 * against the wrong table would miss, or hit a colliding one.
 */
const withSourceOverride = (type: AtlasObjectType, url: URL): AtlasObjectType =>
  type === AtlasObjectType.Organization && url.searchParams.get('source') === 'entity'
    ? AtlasObjectType.Entity
    : type

/**
 * Match a pathname against the path-addressed types, reading the id from the
 * segment right after the route's prefix.
 *
 * Matching on the prefix rather than the whole template is deliberate: a pasted
 * link usually carries a deeper page than the one the id is declared on
 * (`/bbc/<id>/summary/receivables`, `/clients/<id>?tab=Risk`), and all of those
 * still point at the same record.
 */
const parseObjectPath = (url: URL): IAtlasObjectRef | null => {
  const segments = url.pathname.split('/').filter(Boolean)

  for (const { type, prefix } of OBJECT_PATH_MATCHERS) {
    const matchesPrefix = prefix.every((segment, index) => segments[index] === segment)
    const id = segments[prefix.length]
    if (matchesPrefix && isUuid(id)) {
      return { type: withSourceOverride(type, url), id }
    }
  }

  return null
}

/**
 * Identify which Atlas object a URL points at — the inverse of the `atlasUrls`
 * builders above, and the place new types get added as they become resolvable.
 *
 * Task links are matched on the `?taskId` param rather than a path, because the
 * detail panel is hosted by both My Tasks and the project page and a copied link
 * carries whichever page it came from. Every other type is path-addressed.
 *
 * The id must be a well-formed uuid: ids reach here from URLs typed by hand or
 * truncated in transit (an email client wrapping a long link), and entity ids are
 * uuid columns, so a malformed one is a query error downstream rather than a
 * harmless miss. A URL that fails this stays a plain link.
 */
export function parseAtlasObjectUrl(href: string | null | undefined): IAtlasObjectRef | null {
  if (!href || !isInternalAtlasUrl(href)) return null
  try {
    const url = new URL(href, urlBase())
    const taskId = url.searchParams.get('taskId')
    if (isUuid(taskId)) {
      return { type: AtlasObjectType.Task, id: taskId as string }
    }
    return parseObjectPath(url)
  } catch {
    return null
  }
}

/**
 * The name of the page an Atlas URL points at, for a link that names no single
 * record — a list, a dashboard, a settings screen.
 *
 * Callers must try `parseAtlasObjectUrl` first: a record link also matches a
 * route here (`/clients/:id`), and the record is the more specific answer.
 *
 * The matching and the names both come from `getRouteLabel`, which every route
 * feeds with an explicit `label`.
 */
export function parseAtlasPageUrl(href: string | null | undefined): string | null {
  if (!href || !isInternalAtlasUrl(href)) return null
  try {
    const { pathname } = new URL(href, urlBase())
    // A page chip names the page, not the record on it. Without this guard a
    // record type with no resolver renders under its route's generic label —
    // an organization read as "Organization" rather than the company's name,
    // which looks resolved and is wrong. Better to leave it a plain link.
    if (pathname.split('/').some(isUuid)) return null
    return getRouteLabel(pathname) ?? null
  } catch {
    return null
  }
}

/**
 * Whether a path resolves to something Atlas can name — a record, or any
 * declared route.
 *
 * Takes the path rather than a full URL because the two callers establish "is
 * this ours" differently: the browser compares origins, the server compares the
 * host against its configured base. Both then ask this the same question.
 */
export function pathOpensAtlasPage(pathname: string, search = ''): boolean {
  return !!parseAtlasObjectUrl(`${pathname}${search}`) || !!getRouteLabel(pathname)
}

/**
 * The browser-side companion to `pathOpensAtlasPage`: the same question for a
 * full URL, with "is this ours" answered by origin.
 */
export function opensAtlasPage(href: string | null | undefined): boolean {
  if (!href || !isInternalAtlasUrl(href)) return false
  try {
    const { pathname, search } = new URL(href, urlBase())
    return pathOpensAtlasPage(pathname, search)
  } catch {
    return false
  }
}
